import React, { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import { renderAsync } from 'docx-preview';
import * as mammoth from 'mammoth/mammoth.browser';
import { employeeService } from '../../../../services/api';

const DEFAULT_APP_FONT_FAMILY = '"Segoe UI", Inter, Arial, sans-serif';

const LettersManager = () => {
  const [activeLetterType, setActiveLetterType] = useState('offer');
  const [showLetterForm, setShowLetterForm] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedTemplateContent, setUploadedTemplateContent] = useState('');
  const [uploadedTemplateHtml, setUploadedTemplateHtml] = useState('');
  const [uploadedTemplatePreviewHtml, setUploadedTemplatePreviewHtml] = useState('');
  const [uploadedTemplateArrayBuffer, setUploadedTemplateArrayBuffer] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [showAvailableTags, setShowAvailableTags] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplateData, setPreviewTemplateData] = useState(null);
  const docxPreviewContainerRef = useRef(null);
  const docxStyleContainerRef = useRef(null);
  const [docxRenderFailed, setDocxRenderFailed] = useState(false);
  const [activeTextArea, setActiveTextArea] = useState('content');
  const [showAddLetterType, setShowAddLetterType] = useState(false);
  const [newLetterTypeName, setNewLetterTypeName] = useState('');
  const [searchLetterTypes, setSearchLetterTypes] = useState('');
  const [currentLetterTypePage, setCurrentLetterTypePage] = useState(1);
  const [currentTemplatePage, setCurrentTemplatePage] = useState(1);
  const [searchTemplates, setSearchTemplates] = useState('');
  const [searchAvailableTags, setSearchAvailableTags] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // Letter types configuration - now dynamic
  const [letterTypes, setLetterTypes] = useState([
    { id: 'offer', name: 'Offer Letter', icon: '📄', color: 'blue' },
    { id: 'appointment', name: 'Appointment Letter', icon: '📋', color: 'green' },
    { id: 'transfer', name: 'Transfer Letter', icon: '🔄', color: 'purple' },
    { id: 'confirmation', name: 'Confirmation Letter', icon: '✅', color: 'emerald' },
    { id: 'experience', name: 'Experience Letter', icon: '📜', color: 'amber' },
    { id: 'resignation', name: 'Resignation Acceptance Letter', icon: '📝', color: 'red' },
    { id: 'promotion', name: 'Promotion Letter', icon: '⬆️', color: 'indigo' },
    { id: 'salary-increment', name: 'Salary Increment/Revision Letter', icon: '💰', color: 'yellow' },
    { id: 'probation-extension', name: 'Probation Extension Letter', icon: '⏰', color: 'orange' },
    { id: 'termination', name: 'Termination Letter', icon: '❌', color: 'red' },
    { id: 'contract', name: 'Contract Letter', icon: '📋', color: 'teal' },
    { id: 'contract-renewal', name: 'Contract Renewal/Extension Letter', icon: '🔄', color: 'cyan' },
    { id: 'other', name: 'Other Company Letters', icon: '📑', color: 'gray' }
  ]);

  // Sample letter templates
  const [letterTemplates, setLetterTemplates] = useState({
    offer: [
      {
        id: 'offer-1',
        name: 'Template Offer Letter',
        content: `Dear {{employee_name}},

We are pleased to offer you the position of {{designation}} in our {{department}} department.

Position Details:
- Designation: {{designation}}
- Department: {{department}}
- Reporting Manager: {{reporting_manager}}
- Joining Date: {{joining_date}}
- Work Location: {{work_location}}

Compensation Package:
- Basic Salary: ₹{{basic_salary}}
- HRA: ₹{{hra}}
- Special Allowance: ₹{{special_allowance}}
- Total CTC: ₹{{total_ctc}}

Please confirm your acceptance by {{confirmation_date}}.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance'],
          designation: ['manager', 'executive'],
          grade: ['grade-a', 'grade-b']
        }
      }
    ],
    appointment: [
      {
        id: 'appointment-1',
        name: 'Template Appointment Letter',
        content: `Dear {{employee_name}},

This is to confirm your appointment as {{designation}} in our organization effective from {{appointment_date}}.

Your employment details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Reporting Manager: {{reporting_manager}}
- Work Location: {{work_location}}

Terms and Conditions:
- Probation Period: {{probation_period}} months
- Notice Period: {{notice_period}} months
- Working Hours: {{working_hours}}

Compensation:
- Basic Salary: ₹{{basic_salary}}
- Total Package: ₹{{total_ctc}}

Welcome to {{company_name}}!

Best regards,
{{hr_manager_name}}
Human Resources Department`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing'],
          designation: ['manager', 'executive', 'senior-executive'],
          grade: ['grade-a', 'grade-b', 'grade-c']
        }
      }
    ],
    transfer: [
      {
        id: 'transfer-1',
        name: 'Template Transfer Letter',
        content: `Dear {{employee_name}},

This is to inform you that you are being transferred from {{current_department}} to {{new_department}} department effective from {{transfer_date}}.

Transfer Details:
- Current Position: {{current_designation}}
- New Position: {{new_designation}}
- Current Location: {{current_location}}
- New Location: {{new_location}}
- Reporting Manager: {{new_reporting_manager}}

Your salary and benefits will remain unchanged.

Please report to your new department on {{transfer_date}}.

Best regards,
{{hr_manager_name}}
Human Resources Department`,
        assignment: {
          branch: ['main-branch', 'north-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive'],
          grade: ['grade-a', 'grade-b', 'grade-c']
        }
      }
    ],
    confirmation: [
      {
        id: 'confirmation-1',
        name: 'Template Confirmation Letter',
        content: `Dear {{employee_name}},

We are pleased to confirm that your probation period has been successfully completed and you are now a confirmed employee of {{company_name}}.

Confirmation Details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Confirmation Date: {{confirmation_date}}
- Reporting Manager: {{reporting_manager}}

Your performance during the probation period has been satisfactory and we look forward to your continued contribution to the organization.

Congratulations!

Best regards,
{{hr_manager_name}}
Human Resources Department`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['executive', 'senior-executive', 'associate'],
          grade: ['grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    experience: [
      {
        id: 'experience-1',
        name: 'Template Experience Letter',
        content: `To Whom It May Concern,

This is to certify that {{employee_name}} (Employee ID: {{employee_id}}) was employed with {{company_name}} from {{joining_date}} to {{leaving_date}} in the capacity of {{designation}} in the {{department}} department.

During the tenure, {{employee_name}} has been found to be:
- Punctual and regular in attendance
- Honest and sincere in work
- Capable of handling responsibilities
- A team player with good interpersonal skills

{{employee_name}} left the organization on {{leaving_date}} on {{leaving_reason}}.

We wish {{employee_name}} all the best for future endeavors.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive', 'associate'],
          grade: ['grade-a', 'grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    resignation: [
      {
        id: 'resignation-1',
        name: 'Template Resignation Letter',
        content: `Dear {{employee_name}},

We acknowledge receipt of your resignation letter dated {{resignation_date}} and accept your resignation from the position of {{designation}}.

Resignation Details:
- Employee ID: {{employee_id}}
- Last Working Date: {{last_working_date}}
- Notice Period: {{notice_period}} days
- Reason for Leaving: {{resignation_reason}}

We appreciate your contribution to {{company_name}} during your tenure and wish you all the best for your future endeavors.

Please complete the handover process and return all company property before your last working date.

Best regards,
{{hr_manager_name}}
Human Resources Department`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive', 'associate'],
          grade: ['grade-a', 'grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    promotion: [
      {
        id: 'promotion-1',
        name: 'Template Promotion Letter',
        content: `Dear {{employee_name}},

We are pleased to inform you that you have been promoted to the position of {{new_designation}} in our {{department}} department, effective from {{promotion_date}}.

Promotion Details:
- Current Position: {{current_designation}}
- New Position: {{new_designation}}
- Department: {{department}}
- Effective Date: {{promotion_date}}
- Reporting Manager: {{new_reporting_manager}}

This promotion is in recognition of your outstanding performance, dedication, and valuable contributions to the organization.

Your new compensation package will be communicated separately by the HR department.

Congratulations on this well-deserved promotion!

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'senior-manager', 'director'],
          grade: ['grade-a', 'grade-b']
        }
      }
    ],
    'salary-increment': [
      {
        id: 'salary-increment-1',
        name: 'Template Salary Increment Letter',
        content: `Dear {{employee_name}},

We are pleased to inform you that your salary has been revised effective from {{increment_date}}.

Salary Revision Details:
- Employee ID: {{employee_id}}
- Current Designation: {{designation}}
- Department: {{department}}
- Effective Date: {{increment_date}}
- Previous Basic Salary: ₹{{previous_basic_salary}}
- New Basic Salary: ₹{{new_basic_salary}}
- Previous Total CTC: ₹{{previous_total_ctc}}
- New Total CTC: ₹{{new_total_ctc}}
- Increment Percentage: {{increment_percentage}}%

This revision reflects your excellent performance and contribution to the organization.

The revised salary will be reflected in your next salary payment.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'senior-manager', 'director', 'executive'],
          grade: ['grade-a', 'grade-b', 'grade-c']
        }
      }
    ],
    'probation-extension': [
      {
        id: 'probation-extension-1',
        name: 'Template Probation Extension Letter',
        content: `Dear {{employee_name}},

This is to inform you that your probation period has been extended by {{extension_period}} months, effective from {{extension_date}}.

Probation Extension Details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Original Probation Period: {{original_probation_period}} months
- Extended Period: {{extension_period}} months
- New Probation End Date: {{new_probation_end_date}}
- Reason for Extension: {{extension_reason}}

During this extended period, we will continue to evaluate your performance and provide necessary support for your development.

We look forward to your continued improvement and successful completion of the probation period.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['executive', 'senior-executive', 'associate'],
          grade: ['grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    termination: [
      {
        id: 'termination-1',
        name: 'Template Termination Letter',
        content: `Dear {{employee_name}},

This letter serves as formal notice of termination of your employment with {{company_name}}.

Termination Details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Termination Date: {{termination_date}}
- Last Working Date: {{last_working_date}}
- Reason for Termination: {{termination_reason}}
- Notice Period: {{notice_period}} days

Please ensure that all company property is returned and all pending work is completed before your last working date.

We wish you the best for your future endeavors.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive', 'associate'],
          grade: ['grade-a', 'grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    contract: [
      {
        id: 'contract-1',
        name: 'Template Contract Letter',
        content: `Dear {{employee_name}},

We are pleased to offer you a contract position as {{designation}} in our {{department}} department.

Contract Details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Contract Start Date: {{contract_start_date}}
- Contract End Date: {{contract_end_date}}
- Contract Duration: {{contract_duration}} months
- Reporting Manager: {{reporting_manager}}
- Work Location: {{work_location}}

Terms and Conditions:
- Working Hours: {{working_hours}}
- Notice Period: {{notice_period}} days
- Probation Period: {{probation_period}} months

Compensation:
- Basic Salary: ₹{{basic_salary}}
- Total CTC: ₹{{total_ctc}}

Please sign and return this contract to confirm your acceptance.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive', 'associate'],
          grade: ['grade-a', 'grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    'contract-renewal': [
      {
        id: 'contract-renewal-1',
        name: 'Template Contract Renewal Letter',
        content: `Dear {{employee_name}},

We are pleased to inform you that your contract has been renewed for an additional period.

Contract Renewal Details:
- Employee ID: {{employee_id}}
- Designation: {{designation}}
- Department: {{department}}
- Previous Contract End Date: {{previous_contract_end_date}}
- New Contract Start Date: {{new_contract_start_date}}
- New Contract End Date: {{new_contract_end_date}}
- Renewal Duration: {{renewal_duration}} months
- Reporting Manager: {{reporting_manager}}

Terms and Conditions:
- All previous terms and conditions remain the same
- Working Hours: {{working_hours}}
- Notice Period: {{notice_period}} days

Compensation:
- Basic Salary: ₹{{basic_salary}}
- Total CTC: ₹{{total_ctc}}

Please sign and return this renewal contract to confirm your acceptance.

We look forward to your continued contribution to the organization.

Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}`,
        assignment: {
          branch: ['main-branch'],
          department: ['hr', 'it', 'finance', 'marketing', 'sales'],
          designation: ['manager', 'executive', 'senior-executive', 'associate'],
          grade: ['grade-a', 'grade-b', 'grade-c', 'grade-d']
        }
      }
    ],
    other: []
  });

  // Available tags for letter generation
  const availableTags = {
    employee: [
      { tag: '{{employee_name}}', description: 'Full name of the employee' },
      { tag: '{{employee_id}}', description: 'Employee ID' },
      { tag: '{{designation}}', description: 'Current designation' },
      { tag: '{{department}}', description: 'Department name' },
      { tag: '{{reporting_manager}}', description: 'Reporting manager name' },
      { tag: '{{work_location}}', description: 'Work location' },
      { tag: '{{joining_date}}', description: 'Date of joining' },
      { tag: '{{leaving_date}}', description: 'Date of leaving' },
      { tag: '{{current_department}}', description: 'Current department' },
      { tag: '{{new_department}}', description: 'New department' },
      { tag: '{{current_designation}}', description: 'Current designation' },
      { tag: '{{new_designation}}', description: 'New designation' },
      { tag: '{{current_location}}', description: 'Current work location' },
      { tag: '{{new_location}}', description: 'New work location' },
      { tag: '{{new_reporting_manager}}', description: 'New reporting manager' }
    ],
    salary: [
      { tag: '{{basic_salary}}', description: 'Basic salary amount' },
      { tag: '{{hra}}', description: 'House Rent Allowance' },
      { tag: '{{special_allowance}}', description: 'Special allowance' },
      { tag: '{{total_ctc}}', description: 'Total Cost to Company' },
      { tag: '{{gross_salary}}', description: 'Gross salary' },
      { tag: '{{net_salary}}', description: 'Net salary after deductions' }
    ],
    company: [
      { tag: '{{company_name}}', description: 'Company name' },
      { tag: '{{company_address}}', description: 'Company address' },
      { tag: '{{hr_manager_name}}', description: 'HR Manager name' },
      { tag: '{{ceo_name}}', description: 'CEO name' }
    ],
    dates: [
      { tag: '{{appointment_date}}', description: 'Appointment date' },
      { tag: '{{confirmation_date}}', description: 'Confirmation date' },
      { tag: '{{transfer_date}}', description: 'Transfer date' },
      { tag: '{{resignation_date}}', description: 'Resignation date' },
      { tag: '{{last_working_date}}', description: 'Last working date' },
      { tag: '{{probation_period}}', description: 'Probation period' },
      { tag: '{{notice_period}}', description: 'Notice period' },
      { tag: '{{working_hours}}', description: 'Working hours' }
    ],
    other: [
      { tag: '{{leaving_reason}}', description: 'Reason for leaving' },
      { tag: '{{resignation_reason}}', description: 'Resignation reason' }
    ],
    promotion: [
      { tag: '{{new_designation}}', description: 'New designation after promotion' },
      { tag: '{{current_designation}}', description: 'Current designation before promotion' },
      { tag: '{{promotion_date}}', description: 'Date of promotion' },
      { tag: '{{new_reporting_manager}}', description: 'New reporting manager after promotion' }
    ],
    salaryRevision: [
      { tag: '{{increment_date}}', description: 'Date of salary increment' },
      { tag: '{{previous_basic_salary}}', description: 'Previous basic salary amount' },
      { tag: '{{new_basic_salary}}', description: 'New basic salary amount' },
      { tag: '{{previous_total_ctc}}', description: 'Previous total CTC' },
      { tag: '{{new_total_ctc}}', description: 'New total CTC' },
      { tag: '{{increment_percentage}}', description: 'Increment percentage' }
    ],
    probation: [
      { tag: '{{extension_period}}', description: 'Probation extension period in months' },
      { tag: '{{extension_date}}', description: 'Date of probation extension' },
      { tag: '{{original_probation_period}}', description: 'Original probation period' },
      { tag: '{{new_probation_end_date}}', description: 'New probation end date' },
      { tag: '{{extension_reason}}', description: 'Reason for probation extension' }
    ],
    termination: [
      { tag: '{{termination_date}}', description: 'Date of termination' },
      { tag: '{{termination_reason}}', description: 'Reason for termination' }
    ],
    contract: [
      { tag: '{{contract_start_date}}', description: 'Contract start date' },
      { tag: '{{contract_end_date}}', description: 'Contract end date' },
      { tag: '{{contract_duration}}', description: 'Contract duration in months' },
      { tag: '{{previous_contract_end_date}}', description: 'Previous contract end date' },
      { tag: '{{new_contract_start_date}}', description: 'New contract start date' },
      { tag: '{{renewal_duration}}', description: 'Contract renewal duration' }
    ]
  };

  // Sample employees for generation
  const sampleEmployees = [
    { id: 'emp001', name: 'John Doe', employeeId: 'EMP001', designation: 'Manager', department: 'IT', reportingManager: 'Sarah Wilson', workLocation: 'Mumbai' },
    { id: 'emp002', name: 'Jane Smith', employeeId: 'EMP002', designation: 'Senior Executive', department: 'HR', reportingManager: 'Mike Johnson', workLocation: 'Delhi' },
    { id: 'emp003', name: 'Mike Johnson', employeeId: 'EMP003', designation: 'Director', department: 'Finance', reportingManager: 'CEO', workLocation: 'Bangalore' }
  ];
  const employeesForGeneration = employeeOptions;

  useEffect(() => {
    const fetchEmployeesForLetters = async () => {
      try {
        const response = await employeeService.getEmployees({ page: 1, limit: 10000 });
        const list = response?.employees || response?.data || [];
        const normalized = list.map((emp) => ({
          id: String(emp.id || emp._id || emp.empCode || Math.random()),
          name: emp.user?.name || emp.name || emp.fullName || emp.empName || 'Employee',
          employeeId: emp.empCode || emp.employeeId || emp.id || emp._id || 'EMP',
          designation: emp.designation?.name || emp.designationName || emp.designation || '',
          department: emp.department?.name || emp.departmentName || emp.department || '',
          reportingManager:
            emp.reportingManager?.user?.name ||
            emp.reportingManager?.name ||
            emp.manager?.name ||
            emp.reportingManagerName ||
            'Reporting Manager',
          workLocation: emp.branch?.name || emp.location || emp.workLocation || 'N/A',
          company_name: companyData.name,
        }));
        setEmployeeOptions(normalized.filter((e) => e.name));
      } catch (error) {
        console.error('Failed to load employees for letter generation:', error);
      }
    };
    fetchEmployeesForLetters();
  }, []);

  // Company data (would typically come from saved records)
  const companyData = {
    name: 'Your Company Name',
    address: '123 Business Street, City, State, Country - 12345',
    logo: 'https://via.placeholder.com/150x50/0066CC/FFFFFF?text=LOGO',
    phone: '+1-234-567-8900',
    email: 'info@yourcompany.com',
    website: 'www.yourcompany.com'
  };

  const [formData, setFormData] = useState({
    name: '',
    content: '',
    header: '',
    footer: '',
    companyLogo: '',
    companyName: '',
    fontStyle: 'Arial',
    fontSize: '12',
    fontColor: '#000000',
    assignment: {
      branch: [],
      department: [],
      designation: [],
      grade: [],
      subdepartment: [],
      category: [],
      employmentType: []
    }
  });

  const [showOrgLevelsDropdown, setShowOrgLevelsDropdown] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentChange = (category, value) => {
    setFormData(prev => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        [category]: value
      }
    }));
  };

  const handleSaveTemplate = () => {
    const resolvedName = (templateName || formData.name || '').trim();
    const defaultTemplate = getDefaultTemplateForType();
    const resolvedContent = (uploadedTemplateContent || formData.content || defaultTemplate?.content || '').trim();

    if (!resolvedName) {
      alert('Please enter template name');
      return;
    }

    if (!uploadedFile && !editingLetter) {
      alert('Please upload a Word template');
      return;
    }

    if (!resolvedContent) {
      alert('No base content available to save this template');
      return;
    }

    if (editingLetter) {
      // Update existing template
      setLetterTemplates(prev => ({
        ...prev,
        [activeLetterType]: prev[activeLetterType].map(template => 
          template.id === editingLetter.id 
            ? { 
                ...template, 
                name: resolvedName, 
                content: resolvedContent, 
                header: formData.header,
                footer: formData.footer,
                companyLogo: formData.companyLogo,
                companyName: formData.companyName,
                fontStyle: formData.fontStyle,
                fontSize: formData.fontSize,
                fontColor: formData.fontColor,
                assignment: formData.assignment,
                uploadedFileName: uploadedFile?.name || template.uploadedFileName || '' 
              }
            : template
        )
      }));
    } else {
      // Create new template
      const newTemplate = {
        id: `${activeLetterType}-${Date.now()}`,
        name: resolvedName,
        content: resolvedContent,
        header: formData.header,
        footer: formData.footer,
        companyLogo: formData.companyLogo,
        companyName: formData.companyName,
        fontStyle: formData.fontStyle,
        fontSize: formData.fontSize,
        fontColor: formData.fontColor,
        assignment: formData.assignment,
        uploadedFileName: uploadedFile?.name || ''
      };

      setLetterTemplates(prev => ({
        ...prev,
        [activeLetterType]: [...prev[activeLetterType], newTemplate]
      }));
    }

    setFormData({
      name: '',
      content: '',
      header: '',
      footer: '',
      companyLogo: '',
      companyName: '',
      fontStyle: 'Arial',
      fontSize: '12',
      fontColor: '#000000',
      assignment: {
        branch: [],
        department: [],
        designation: [],
        grade: [],
        subdepartment: [],
        category: [],
        employmentType: []
      }
    });
    setShowLetterForm(false);
    setEditingLetter(null);
    setShowAvailableTags(false);
    setUploadedFile(null);
    setUploadedTemplateContent('');
    setUploadedTemplateHtml('');
    setUploadedTemplatePreviewHtml('');
    setUploadedTemplateArrayBuffer(null);
    setTemplateName('');
  };

  const handleDeleteTemplate = (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      setLetterTemplates(prev => ({
        ...prev,
        [activeLetterType]: prev[activeLetterType].filter(template => template.id !== templateId)
      }));
    }
  };

  const handleCopyTemplate = (template) => {
    const copiedTemplate = {
      ...template,
      id: `${activeLetterType}-${Date.now()}`,
      name: `${template.name} (Copy)`,
    };

    setLetterTemplates((prev) => ({
      ...prev,
      [activeLetterType]: [copiedTemplate, ...(prev[activeLetterType] || [])],
    }));
  };

  const handleDownloadTemplate = (template) => {
    const blob = new Blob([template.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const replaceTags = (content, employee) => {
    let generatedContent = content;
    
    // Replace tags with actual values
    Object.entries(availableTags).forEach(([category, tags]) => {
      tags.forEach(({ tag }) => {
        const tagName = tag.replace(/[{}]/g, '');
        let value = '';
        
        switch (tagName) {
          case 'employee_name':
            value = employee.name;
            break;
          case 'employee_id':
            value = employee.employeeId;
            break;
          case 'designation':
            value = employee.designation;
            break;
          case 'department':
            value = employee.department;
            break;
          case 'reporting_manager':
            value = employee.reportingManager;
            break;
          case 'work_location':
            value = employee.workLocation;
            break;
          case 'company_name':
            value = employee.company_name || 'Your Company Name';
            break;
          case 'hr_manager_name':
            value = employee.hr_manager_name || 'HR Manager';
            break;
          case 'basic_salary':
            value = employee.basic_salary || '50000';
            break;
          case 'hra':
            value = employee.hra || '20000';
            break;
          case 'total_ctc':
            value = employee.total_ctc || '800000';
            break;
          case 'joining_date':
            value = employee.joining_date || '2024-01-15';
            break;
          case 'appointment_date':
            value = employee.appointment_date || '2024-01-15';
            break;
          case 'confirmation_date':
            value = employee.confirmation_date || '2024-04-15';
            break;
          case 'transfer_date':
            value = employee.transfer_date || '2024-06-01';
            break;
          case 'resignation_date':
            value = employee.resignation_date || '2024-12-01';
            break;
          case 'last_working_date':
            value = employee.last_working_date || '2024-12-31';
            break;
          case 'probation_period':
            value = employee.probation_period || '3';
            break;
          case 'notice_period':
            value = employee.notice_period || '30';
            break;
          case 'working_hours':
            value = employee.working_hours || '9:00 AM - 6:00 PM';
            break;
          case 'leaving_reason':
            value = employee.leaving_reason || 'Personal reasons';
            break;
          case 'resignation_reason':
            value = employee.resignation_reason || 'Career growth';
            break;
          case 'new_designation':
            value = employee.new_designation || 'Senior Manager';
            break;
          case 'current_designation':
            value = employee.current_designation || 'Manager';
            break;
          case 'promotion_date':
            value = employee.promotion_date || '2024-07-01';
            break;
          case 'new_reporting_manager':
            value = employee.new_reporting_manager || 'Director';
            break;
          case 'increment_date':
            value = employee.increment_date || '2024-07-01';
            break;
          case 'previous_basic_salary':
            value = employee.previous_basic_salary || '50000';
            break;
          case 'new_basic_salary':
            value = employee.new_basic_salary || '60000';
            break;
          case 'previous_total_ctc':
            value = employee.previous_total_ctc || '800000';
            break;
          case 'new_total_ctc':
            value = employee.new_total_ctc || '900000';
            break;
          case 'increment_percentage':
            value = employee.increment_percentage || '12.5';
            break;
          case 'extension_period':
            value = employee.extension_period || '3';
            break;
          case 'extension_date':
            value = employee.extension_date || '2024-07-01';
            break;
          case 'original_probation_period':
            value = employee.original_probation_period || '6';
            break;
          case 'new_probation_end_date':
            value = employee.new_probation_end_date || '2024-10-01';
            break;
          case 'extension_reason':
            value = employee.extension_reason || 'Performance evaluation pending';
            break;
          case 'termination_date':
            value = employee.termination_date || '2024-12-31';
            break;
          case 'termination_reason':
            value = employee.termination_reason || 'Company restructuring';
            break;
          case 'contract_start_date':
            value = employee.contract_start_date || '2024-01-01';
            break;
          case 'contract_end_date':
            value = employee.contract_end_date || '2024-12-31';
            break;
          case 'contract_duration':
            value = employee.contract_duration || '12';
            break;
          case 'previous_contract_end_date':
            value = employee.previous_contract_end_date || '2024-12-31';
            break;
          case 'new_contract_start_date':
            value = employee.new_contract_start_date || '2025-01-01';
            break;
          case 'renewal_duration':
            value = employee.renewal_duration || '12';
            break;
          default:
            value = `[${tagName}]`;
        }
        
        // Use a more robust replacement that works with HTML content
        const regex = new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        generatedContent = generatedContent.replace(regex, value);
      });
    });

    return generatedContent;
  };

  const escapeHtml = (text = '') =>
    String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toPreviewHtml = (text = '') => escapeHtml(text).replace(/\n/g, '<br/>');

  const getHypotheticalEmployee = (companyNameOverride) => ({
    name: 'John Doe',
    employeeId: 'EMP001',
    designation: 'Manager',
    department: 'IT',
    reportingManager: 'Sarah Wilson',
    workLocation: 'Mumbai',
    basic_salary: '50000',
    hra: '20000',
    total_ctc: '800000',
    company_name: companyNameOverride || companyData.name,
    hr_manager_name: 'HR Manager',
    joining_date: '2024-01-15',
    appointment_date: '2024-01-15',
    confirmation_date: '2024-04-15',
    transfer_date: '2024-06-01',
    resignation_date: '2024-12-01',
    last_working_date: '2024-12-31',
    probation_period: '3',
    notice_period: '30',
    working_hours: '9:00 AM - 6:00 PM',
    leaving_reason: 'Personal reasons',
    resignation_reason: 'Career growth'
  });

  const getTagValueMap = (employeeData) => {
    const allTags = Object.values(availableTags).flat().map((x) => x.tag);
    const map = {};
    allTags.forEach((tag) => {
      map[tag] = replaceTags(tag, employeeData);
    });
    return map;
  };

  const extractDocxTextFallback = async (arrayBuffer) => {
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      const decodeXmlEntities = (input = '') =>
        input
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'");

      const collectTextFromXml = async (path) => {
        const file = zip.file(path);
        if (!file) return '';
        const xml = await file.async('text');

        // Primary: regex paragraph extraction for namespace-tolerant parsing.
        const paragraphRegex = /<w:p\b[\s\S]*?<\/w:p>/g;
        const textRegex = /<(w:t|a:t|w:instrText)\b[^>]*>([\s\S]*?)<\/\1>/g;
        const paragraphMatches = xml.match(paragraphRegex) || [];

        if (paragraphMatches.length > 0) {
          const lines = paragraphMatches.map((p) => {
            const parts = [];
            let m;
            while ((m = textRegex.exec(p)) !== null) {
              parts.push(decodeXmlEntities(m[2] || ''));
            }
            return parts.join('');
          });
          const joined = lines.join('\n').trim();
          if (joined) return joined;
        }

        // Secondary: plain text-node extraction across whole XML.
        const allParts = [];
        let m;
        while ((m = textRegex.exec(xml)) !== null) {
          allParts.push(decodeXmlEntities(m[2] || ''));
        }
        return allParts.join(' ').trim();
      };

      const docText = await collectTextFromXml('word/document.xml');
      const headerFiles = Object.keys(zip.files).filter((k) => /^word\/header\d+\.xml$/.test(k));
      const footerFiles = Object.keys(zip.files).filter((k) => /^word\/footer\d+\.xml$/.test(k));

      const headerTextParts = await Promise.all(headerFiles.map((f) => collectTextFromXml(f)));
      const footerTextParts = await Promise.all(footerFiles.map((f) => collectTextFromXml(f)));

      const headerText = headerTextParts.filter(Boolean).join('\n');
      const footerText = footerTextParts.filter(Boolean).join('\n');
      const combined = [headerText, docText, footerText].filter(Boolean).join('\n\n').trim();

      return {
        text: combined,
        html: combined ? toPreviewHtml(combined) : '',
      };
    } catch (e) {
      console.error('DOCX fallback text extraction failed:', e);
      return { text: '', html: '' };
    }
  };

  useEffect(() => {
    const renderDocxPreview = async () => {
      if (!showTemplatePreview || !previewTemplateData?.useDocxRender) return;
      if (!uploadedTemplateArrayBuffer || !docxPreviewContainerRef.current) return;

      try {
        setDocxRenderFailed(false);
        docxPreviewContainerRef.current.innerHTML = '';
        await renderAsync(
          uploadedTemplateArrayBuffer,
          docxPreviewContainerRef.current,
          docxStyleContainerRef.current || docxPreviewContainerRef.current,
          {
            className: 'docx-preview',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
            useBase64URL: true,
          }
        );

        // Replace tags in rendered DOCX preview with hypothetical values.
        const valueMap = previewTemplateData?.tagValueMap || {};
        if (Object.keys(valueMap).length > 0) {
          const walker = document.createTreeWalker(
            docxPreviewContainerRef.current,
            NodeFilter.SHOW_TEXT
          );
          const textNodes = [];
          let node = walker.nextNode();
          while (node) {
            textNodes.push(node);
            node = walker.nextNode();
          }
          textNodes.forEach((textNode) => {
            if (!textNode?.nodeValue) return;
            let updated = textNode.nodeValue;
            Object.entries(valueMap).forEach(([tag, val]) => {
              updated = updated.split(tag).join(String(val));
            });
            if (updated !== textNode.nodeValue) {
              textNode.nodeValue = updated;
            }
          });
        }

        const renderedText = (docxPreviewContainerRef.current.textContent || '').trim();
        if (!renderedText) {
          setDocxRenderFailed(true);
        }
      } catch (error) {
        console.error('DOCX preview rendering failed:', error);
        setDocxRenderFailed(true);
      }
    };

    renderDocxPreview();
  }, [showTemplatePreview, previewTemplateData, uploadedTemplateArrayBuffer]);

  const handlePreviewLetter = (template, employee) => {
    const previewEmployee = employee || getHypotheticalEmployee(template.companyName || companyData.name);
    const tagValueMap = getTagValueMap(previewEmployee);
    const resolvedHeader = template.header ? replaceTags(template.header, previewEmployee) : '';
    const resolvedContent = replaceTags(template.content || '', previewEmployee);
    const resolvedFooter = template.footer ? replaceTags(template.footer, previewEmployee) : '';

    const richContentHtml = `
      ${resolvedHeader ? `<div style="text-align:center; margin-bottom:20px; border-bottom:2px solid #ddd; padding-bottom:10px;">${resolvedHeader}</div>` : ''}
      <div style="white-space:pre-wrap; line-height:1.6;">${resolvedContent}</div>
      ${resolvedFooter ? `<div style="margin-top:20px; border-top:2px solid #ddd; padding-top:10px;">${resolvedFooter}</div>` : ''}
    `;

    setPreviewTemplateData({
      ...template,
      name: template.name,
      content: resolvedContent,
      contentHtml: toPreviewHtml(resolvedContent),
      richContentHtml,
      useDocxRender: false,
      previewMode: 'template',
      tagValueMap,
      uploadedFileName: template.uploadedFileName || '',
    });
    setDocxRenderFailed(false);
    setShowTemplatePreview(true);
  };

  const handleGenerateLetter = (template, employee) => {
    const generatedContent = replaceTags(template.content, employee);
    const header = template.header ? replaceTags(template.header, employee) : '';
    const footer = template.footer ? replaceTags(template.footer, employee) : '';
    
    // Create a complete letter with header, content, and footer
    const fullLetter = `
      <div style='font-family: ${template.fontStyle || DEFAULT_APP_FONT_FAMILY}; font-size: ${template.fontSize || '12'}px; color: ${template.fontColor || '#000000'};'>
        ${header ? `<div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">${header}</div>` : ''}
        <div style="white-space: pre-wrap; line-height: 1.6;">${generatedContent}</div>
        ${footer ? `<div style="text-align: center; margin-top: 20px; border-top: 2px solid #ccc; padding-top: 10px;">${footer}</div>` : ''}
      </div>
    `;
    
    // Open PDF in new window
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head>
          <title>${template.name} - ${employee.name}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: ${template.fontStyle || DEFAULT_APP_FONT_FAMILY}; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          ${fullLetter}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name || '';
    const isWordByType =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword';
    const isWordByName = /\.(docx|doc)$/i.test(fileName);

    if (!isWordByType && !isWordByName) {
      alert('Please upload a valid Word document (.doc or .docx)');
      return;
    }

    setUploadedFile(file);
    setUploadedTemplateContent('');
    setUploadedTemplateHtml('');
    setUploadedTemplatePreviewHtml('');
    setUploadedTemplateArrayBuffer(null);
    setTemplateName((prev) => prev || fileName.replace(/\.(docx|doc)$/i, ''));

    try {
      if (/\.docx$/i.test(fileName)) {
        const arrayBuffer = await file.arrayBuffer();
        setUploadedTemplateArrayBuffer(arrayBuffer);
        // Keep text/html fallback optional; primary preview is exact DOCX render.
        setUploadedTemplateContent('');
        setUploadedTemplateHtml('');
        try {
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              convertImage: mammoth.images.inline(async (image) => ({
                src: await image.read('base64').then((data) => `data:${image.contentType};base64,${data}`),
              })),
            }
          );
          setUploadedTemplatePreviewHtml(result?.value || '');
        } catch (htmlErr) {
          console.warn('Mammoth HTML conversion failed:', htmlErr);
        }
      } else {
        alert('Exact preview is supported for .docx format. Please upload the file in .docx format.');
      }
    } catch (err) {
      console.error('Failed to read uploaded file buffer', err);
      alert('Unable to read the uploaded file buffer. Please try uploading again.');
    }
  };

  const getDefaultTemplateForType = () => {
    const templates = letterTemplates[activeLetterType] || [];
    return templates[0] || null;
  };

  const handleDownloadSampleFormat = () => {
    const sampleTemplate = getDefaultTemplateForType();
    if (!sampleTemplate) {
      alert('No sample format available for this letter type.');
      return;
    }

    const extractTagsFromText = (text = '') => {
      const matches = String(text).match(/\{\{[^}]+\}\}/g) || [];
      return Array.from(new Set(matches));
    };

    const allTagDefinitions = Object.values(availableTags).flat();
    const usedTagsInTemplate = extractTagsFromText(
      `${sampleTemplate.header || ''}\n${sampleTemplate.content || ''}\n${sampleTemplate.footer || ''}`
    );

    const applicableTags = usedTagsInTemplate.map((tag) => {
      const def = allTagDefinitions.find((x) => x.tag === tag);
      return {
        tag,
        description: def?.description || 'Dynamic field',
      };
    });

    const typeName = letterTypes.find((t) => t.id === activeLetterType)?.name || activeLetterType;
    const today = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const headerHtml = sampleTemplate.header
      ? `<div style="margin-bottom: 20px; white-space: pre-wrap;">${sampleTemplate.header}</div>`
      : `<div style="margin-bottom: 20px; text-align: center;">
           <div style="font-size: 20px; font-weight: 700;">{{company_name}}</div>
           <div style="font-size: 12px; color: #555;">{{company_address}}</div>
         </div>`;

    const footerHtml = sampleTemplate.footer
      ? `<div style="margin-top: 24px; white-space: pre-wrap;">${sampleTemplate.footer}</div>`
      : `<div style="margin-top: 24px; white-space: pre-wrap;">
Best regards,
{{hr_manager_name}}
Human Resources Department
{{company_name}}
         </div>`;

    const tagsRows = applicableTags.length
      ? applicableTags
          .map(
            (x) => `<tr>
              <td style="border:1px solid #d1d5db;padding:6px 8px;font-family:Consolas,monospace;">${x.tag}</td>
              <td style="border:1px solid #d1d5db;padding:6px 8px;">${x.description}</td>
            </tr>`
          )
          .join("")
      : `<tr><td colspan="2" style="border:1px solid #d1d5db;padding:6px 8px;">No dynamic tags detected in this sample.</td></tr>`;

    const htmlDoc = `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            @page { size: A4; margin: 28mm 22mm 24mm 22mm; }
            body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12pt; line-height: 1.5; color: #111827; }
            .doc-wrap { max-width: 760px; margin: 0 auto; }
            .meta { text-align: right; margin-bottom: 14px; }
            .title { text-align: center; font-size: 16pt; font-weight: 700; margin: 10px 0 14px; }
            .letter-body { white-space: pre-wrap; text-align: left; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 11pt; }
            th { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; background: #f3f4f6; }
          </style>
        </head>
        <body>
          <div class="doc-wrap">
            <div class="meta">Date: ${today}</div>
            <div class="title">${typeName} - Sample Format</div>
            ${headerHtml}
            <div class="letter-body">${sampleTemplate.content || ""}</div>
            ${footerHtml}
            <table>
              <thead>
                <tr>
                  <th style="width:40%;">Applicable Tag</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                ${tagsRows}
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlDoc], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${typeName}-Sample-Format.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleUploadedTemplatePreview = async () => {
    if (!uploadedFile) {
      alert('Please upload a Word template first.');
      return;
    }

    const sampleEmployee = getHypotheticalEmployee();

    const tagValueMap = getTagValueMap(sampleEmployee);

    let previewBaseContent = uploadedTemplateContent || '';
    let previewBaseHtml = uploadedTemplateHtml || '';

    let canUseDocxRender = false;
    let previewArrayBuffer = uploadedTemplateArrayBuffer;
    const uploadedFileName = uploadedFile?.name || '';

    if (/\.docx$/i.test(uploadedFileName) && uploadedFile) {
      try {
        // Re-read at preview time to avoid stale state issues.
        previewArrayBuffer = await uploadedFile.arrayBuffer();
        setUploadedTemplateArrayBuffer(previewArrayBuffer);
        canUseDocxRender = true;

        if (!previewBaseContent && !previewBaseHtml && previewArrayBuffer) {
          const fallback = await extractDocxTextFallback(previewArrayBuffer);
          previewBaseContent = fallback.text || '';
          previewBaseHtml = fallback.html || '';
          setUploadedTemplateContent(previewBaseContent);
          setUploadedTemplateHtml(previewBaseHtml);
        }
      } catch (e) {
        console.error('Failed to read uploaded docx buffer for preview:', e);
        canUseDocxRender = false;
      }
    } else if (uploadedTemplateArrayBuffer) {
      canUseDocxRender = true;
    }

    const previewData = {
      id: `upload-preview-${Date.now()}`,
      name: templateName || uploadedFile.name.replace(/\.(docx|doc)$/i, '') || 'Uploaded Template',
      content: replaceTags(previewBaseContent, sampleEmployee),
      contentHtml: previewBaseHtml
        ? replaceTags(previewBaseHtml, sampleEmployee)
        : toPreviewHtml(replaceTags(previewBaseContent, sampleEmployee)),
      richContentHtml: uploadedTemplatePreviewHtml
        ? replaceTags(uploadedTemplatePreviewHtml, sampleEmployee)
        : '',
      useDocxRender: canUseDocxRender,
      previewMode: 'uploaded',
      tagValueMap,
      // Uploaded format preview should reflect uploaded file body only.
      header: '',
      footer: '',
      uploadedFileName: uploadedFile?.name || ''
    };

    setDocxRenderFailed(false);
    setPreviewTemplateData(previewData);
    setShowTemplatePreview(true);
  };

  const handleTemplateCardPreview = (template) => {
    const sampleEmployee = getHypotheticalEmployee(template.companyName || companyData.name);
    const tagValueMap = getTagValueMap(sampleEmployee);
    const resolvedHeader = template.header ? replaceTags(template.header, sampleEmployee) : '';
    const resolvedContent = replaceTags(template.content || '', sampleEmployee);
    const resolvedFooter = template.footer ? replaceTags(template.footer, sampleEmployee) : '';

    const richContentHtml = `
      ${resolvedHeader ? `<div style="text-align:center; margin-bottom:20px; border-bottom:2px solid #ddd; padding-bottom:10px;">${resolvedHeader}</div>` : ''}
      <div style="white-space:pre-wrap; line-height:1.6;">${resolvedContent}</div>
      ${resolvedFooter ? `<div style="margin-top:20px; border-top:2px solid #ddd; padding-top:10px;">${resolvedFooter}</div>` : ''}
    `;

    setPreviewTemplateData({
      ...template,
      name: template.name,
      content: resolvedContent,
      contentHtml: toPreviewHtml(resolvedContent),
      richContentHtml,
      useDocxRender: false,
      previewMode: 'template',
      tagValueMap,
      uploadedFileName: '',
    });
    setDocxRenderFailed(false);
    setShowTemplatePreview(true);
  };

  const handleTemplatePreview = () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      alert('Please fill in template name and content before preview');
      return;
    }
    
    // Create a sample employee for preview
    const sampleEmployee = {
      name: 'John Doe',
      employeeId: 'EMP001',
      designation: 'Manager',
      department: 'IT',
      reportingManager: 'Sarah Wilson',
      workLocation: 'Mumbai',
      basic_salary: '50000',
      hra: '20000',
      total_ctc: '800000',
      company_name: formData.companyName || companyData.name,
      hr_manager_name: 'HR Manager',
      joining_date: '2024-01-15',
      appointment_date: '2024-01-15',
      confirmation_date: '2024-04-15',
      transfer_date: '2024-06-01',
      resignation_date: '2024-12-01',
      last_working_date: '2024-12-31',
      probation_period: '3',
      notice_period: '30',
      working_hours: '9:00 AM - 6:00 PM',
      leaving_reason: 'Personal reasons',
      resignation_reason: 'Career growth'
    };
    
    // Replace tags with dummy data
    const previewData = {
      ...formData,
      content: replaceTags(formData.content, sampleEmployee),
      header: formData.header ? replaceTags(formData.header, sampleEmployee) : '',
      footer: formData.footer ? replaceTags(formData.footer, sampleEmployee) : ''
    };
    
    setPreviewTemplateData(previewData);
    setShowTemplatePreview(true);
  };

  const insertTag = (tag) => {
    const element = document.getElementById(activeTextArea);
    if (element) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(tag);
        range.insertNode(textNode);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append at the end
        element.innerHTML += tag;
      }
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        [activeTextArea]: element.innerHTML
      }));
    }
  };

  const applyFormatting = (format, value) => {
    const element = document.getElementById(activeTextArea);
    if (element) {
      // Focus the element first
      element.focus();
      
      if (format === 'pagebreak') {
        // Insert page break at cursor position
        const pageBreak = document.createElement('div');
        pageBreak.style.pageBreakBefore = 'always';
        pageBreak.style.height = '0';
        pageBreak.style.margin = '0';
        pageBreak.style.padding = '0';
        pageBreak.innerHTML = '<br>';
        
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(pageBreak);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Update form data
        setFormData(prev => ({
          ...prev,
          [activeTextArea]: element.innerHTML
        }));
      } else if (format === 'fontStyle' || format === 'fontSize') {
        // Apply to entire content
        element.style.fontFamily = format === 'fontStyle' ? value : element.style.fontFamily;
        element.style.fontSize = format === 'fontSize' ? `${value}px` : element.style.fontSize;
        
        setFormData(prev => ({
          ...prev,
          [activeTextArea]: element.innerHTML
        }));
      } else {
        // Use document.execCommand for better MS Word-like behavior
        let success = false;
        
        switch (format) {
          case 'bold':
            success = document.execCommand('bold', false, null);
            break;
          case 'italic':
            success = document.execCommand('italic', false, null);
            break;
          case 'underline':
            success = document.execCommand('underline', false, null);
            break;
          case 'color': {
            const color = value || prompt('Enter color (e.g., #FF0000 or blue):', '#0000FF');
            if (color) {
              success = document.execCommand('foreColor', false, color);
            } else {
              return;
            }
            break;
          }
          case 'justifyLeft':
            success = document.execCommand('justifyLeft', false, null);
            break;
          case 'justifyCenter':
            success = document.execCommand('justifyCenter', false, null);
            break;
          case 'justifyRight':
            success = document.execCommand('justifyRight', false, null);
            break;
          case 'justifyFull':
            success = document.execCommand('justifyFull', false, null);
            break;
          default:
            return;
        }
        
        if (success) {
          // Update form data after successful formatting
          setFormData(prev => ({
            ...prev,
            [activeTextArea]: element.innerHTML
          }));
        } else if (format !== 'justifyLeft' && format !== 'justifyCenter' && format !== 'justifyRight' && format !== 'justifyFull') {
          alert('Please select text to format');
        }
      }
    }
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-redhalo-mint/40 border-redhalo-teal/50 text-redhalo-navy',
      green: 'bg-green-50 border-green-200 text-green-900',
      purple: 'bg-purple-50 border-purple-200 text-purple-900',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
      amber: 'bg-amber-50 border-amber-200 text-amber-900',
      red: 'bg-red-50 border-red-200 text-red-900',
      gray: 'bg-gray-50 border-gray-200 text-gray-900'
    };
    return colorMap[color] || colorMap.gray;
  };

  const handleAddLetterType = () => {
    if (!newLetterTypeName.trim()) {
      alert('Please enter a letter type name');
      return;
    }
    
    const newType = {
      id: newLetterTypeName.toLowerCase().replace(/\s+/g, '-'),
      name: newLetterTypeName,
      icon: '📄',
      color: 'gray'
    };
    
    setLetterTypes(prev => [...prev, newType]);
    setLetterTemplates(prev => ({
      ...prev,
      [newType.id]: []
    }));
    setNewLetterTypeName('');
    setShowAddLetterType(false);
  };

  const getFilteredLetterTypes = () => {
    return letterTypes.filter(type => 
      type.name.toLowerCase().includes(searchLetterTypes.toLowerCase())
    );
  };

  const getPaginatedLetterTypes = () => {
    const filtered = getFilteredLetterTypes();
    const startIndex = (currentLetterTypePage - 1) * 5;
    return filtered.slice(startIndex, startIndex + 5);
  };

  const getFilteredTemplates = () => {
    const templates = letterTemplates[activeLetterType] || [];
    return templates.filter(template => 
      template.name.toLowerCase().includes(searchTemplates.toLowerCase())
    );
  };

  const getPaginatedTemplates = () => {
    const filtered = getFilteredTemplates();
    const startIndex = (currentTemplatePage - 1) * 5;
    return filtered.slice(startIndex, startIndex + 5);
  };

  const getFilteredAvailableTags = () => {
    if (!searchAvailableTags.trim()) {
      return availableTags;
    }
    
    const filtered = {};
    Object.entries(availableTags).forEach(([category, tags]) => {
      const filteredTags = tags.filter(({ tag, description }) => 
        tag.toLowerCase().includes(searchAvailableTags.toLowerCase()) ||
        description.toLowerCase().includes(searchAvailableTags.toLowerCase())
      );
      if (filteredTags.length > 0) {
        filtered[category] = filteredTags;
      }
    });
    return filtered;
  };

  return (
    <div className="space-y-6">
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
        }
        [contenteditable]:focus:before {
          content: none;
        }
        [contenteditable] {
          outline: none;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
        }
        [contenteditable] * {
          margin: 0;
          padding: 0;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
        }
        [contenteditable] p {
          margin: 0;
          padding: 0;
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] br {
          line-height: 1.6;
        }
        [contenteditable] strong, [contenteditable] b {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] em, [contenteditable] i {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] u {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] span {
          direction: ltr !important;
          text-align: left !important;
        }
      `}</style>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Letters Management
        </h2>
        <p className="text-gray-600">
          Create, manage, and generate various company letters
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side - Letter Types */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Letter Types</h3>
              <button
                onClick={() => setShowAddLetterType(true)}
                className="px-3 py-1 text-sm font-medium text-redhalo-navy bg-redhalo-mint/60 rounded-md hover:bg-redhalo-mint"
              >
                + Add New
              </button>
            </div>
            
            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search letter types..."
                value={searchLetterTypes}
                onChange={(e) => {
                  setSearchLetterTypes(e.target.value);
                  setCurrentLetterTypePage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
              />
            </div>
            
            {/* Letter Types List with Pagination */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {getPaginatedLetterTypes().map((type) => (
                <button
                  key={type.id}
                  onClick={() => {
                    setActiveLetterType(type.id);
                    setCurrentTemplatePage(1);
                    setSearchTemplates('');
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    activeLetterType === type.id
                      ? getColorClasses(type.color)
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{type.icon}</span>
                    <div>
                      <div className="font-medium">{type.name}</div>
                      <div className="text-sm text-gray-500">
                        {letterTemplates[type.id]?.length || 0} templates
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Pagination for Letter Types */}
            {getFilteredLetterTypes().length > 5 && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentLetterTypePage(prev => Math.max(1, prev - 1))}
                  disabled={currentLetterTypePage === 1}
                  className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentLetterTypePage} of {Math.ceil(getFilteredLetterTypes().length / 5)}
                </span>
                <button
                  onClick={() => setCurrentLetterTypePage(prev => Math.min(Math.ceil(getFilteredLetterTypes().length / 5), prev + 1))}
                  disabled={currentLetterTypePage >= Math.ceil(getFilteredLetterTypes().length / 5)}
                  className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Right Side - Templates and Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Templates List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {letterTypes.find(t => t.id === activeLetterType)?.name} Templates
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingLetter(null);
                    setUploadedFile(null);
                    setUploadedTemplateContent('');
                    setUploadedTemplateHtml('');
                    setUploadedTemplatePreviewHtml('');
                    setUploadedTemplateArrayBuffer(null);
                    setTemplateName('');
                    setShowAvailableTags(true);
                    setFormData(prev => ({
                      ...prev,
                      name: '',
                      content: '',
                    }));
                    setShowLetterForm(true);
                  }}
                  className="px-3 py-2 text-sm font-medium text-white bg-redhalo-red rounded-md hover:opacity-95"
                >
                  Create New Template
                </button>
              </div>
            </div>

            {/* Search Templates */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTemplates}
                onChange={(e) => {
                  setSearchTemplates(e.target.value);
                  setCurrentTemplatePage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
              />
            </div>

            {/* Templates List with Pagination */}
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {getPaginatedTemplates().map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowGenerateModal(true);
                        }}
                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                      >
                        Generate
                      </button>
                      <button
                        onClick={() => handleTemplateCardPreview(template)}
                        className="px-3 py-1 text-xs font-medium text-redhalo-navy bg-redhalo-mint/60 rounded hover:bg-redhalo-mint"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleCopyTemplate(template)}
                        className="px-3 py-1 text-xs font-medium text-redhalo-navy bg-redhalo-mint rounded hover:bg-redhalo-teal/50"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          setEditingLetter(template);
                          setUploadedFile(null);
                          setUploadedTemplateContent('');
                          setUploadedTemplateHtml('');
                          setUploadedTemplatePreviewHtml('');
                          setUploadedTemplateArrayBuffer(null);
                          setTemplateName(template.name || '');
                          setFormData({
                            name: template.name,
                            content: template.content,
                            header: template.header || '',
                            footer: template.footer || '',
                            companyLogo: template.companyLogo || '',
                            companyName: template.companyName || '',
                            fontStyle: template.fontStyle || 'Arial',
                            fontSize: template.fontSize || '12',
                            fontColor: template.fontColor || '#000000',
                            assignment: template.assignment
                          });
                          setShowLetterForm(true);
                        }}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                      >
                        Upload Template
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {getFilteredTemplates().length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {searchTemplates ? 'No templates found matching your search' : 'No templates available for this letter type'}
                </div>
              )}
            </div>

            {/* Pagination for Templates */}
            {getFilteredTemplates().length > 5 && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentTemplatePage(prev => Math.max(1, prev - 1))}
                  disabled={currentTemplatePage === 1}
                  className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentTemplatePage} of {Math.ceil(getFilteredTemplates().length / 5)}
                </span>
                <button
                  onClick={() => setCurrentTemplatePage(prev => Math.min(Math.ceil(getFilteredTemplates().length / 5), prev + 1))}
                  disabled={currentTemplatePage >= Math.ceil(getFilteredTemplates().length / 5)}
                  className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Template Modal */}
      {showLetterForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[92vw] max-w-5xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLetter ? 'Upload Template Format' : 'Add Template Format'}
              </h2>
              <button
                onClick={() => {
                  setShowLetterForm(false);
                  setEditingLetter(null);
                  setShowAvailableTags(false);
                  setUploadedFile(null);
                  setUploadedTemplateContent('');
                  setUploadedTemplateHtml('');
                  setUploadedTemplatePreviewHtml('');
                  setUploadedTemplateArrayBuffer(null);
                  setTemplateName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload MS Word File *</label>
                  <input
                    type="file"
                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="rounded-md border border-redhalo-teal/50 bg-redhalo-mint/40 p-3 text-sm text-redhalo-navy">
                {uploadedFile
                  ? `Uploaded: ${uploadedFile.name}`
                  : (editingLetter?.uploadedFileName
                      ? `Current uploaded file: ${editingLetter.uploadedFileName}`
                      : 'No Word file uploaded yet.')}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownloadSampleFormat}
                  className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200"
                >
                  Download Sample Format
                </button>
                <button
                  onClick={handleUploadedTemplatePreview}
                  className="px-4 py-2 text-sm font-medium text-redhalo-navy bg-redhalo-mint/60 rounded-md hover:bg-redhalo-mint"
                >
                  Preview Uploaded Format
                </button>
                <button
                  onClick={() => setShowAvailableTags((v) => !v)}
                  className="px-4 py-2 text-sm font-medium text-redhalo-navy bg-redhalo-mint rounded-md hover:bg-redhalo-teal/50"
                >
                  {showAvailableTags ? 'Hide Available Tags' : 'Show Available Tags'}
                </button>
              </div>

              {showAvailableTags && (
                <div className="border border-gray-200 rounded-md p-4">
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search tags..."
                      value={searchAvailableTags}
                      onChange={(e) => setSearchAvailableTags(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-72 overflow-y-auto">
                    {Object.entries(getFilteredAvailableTags()).map(([category, tags]) => (
                      <div key={category}>
                        <h4 className="text-sm font-semibold text-gray-800 mb-2 capitalize">{category}</h4>
                        <div className="space-y-1">
                          {tags.map(({ tag, description }) => (
                            <div key={tag} className="text-xs text-gray-700 border border-gray-200 rounded px-2 py-1">
                              <code className="bg-gray-100 px-1 rounded mr-1">{tag}</code>
                              {description}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end items-center p-6 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowLetterForm(false);
                    setEditingLetter(null);
                    setShowAvailableTags(false);
                    setUploadedFile(null);
                    setUploadedTemplateContent('');
                    setUploadedTemplateHtml('');
                    setUploadedTemplatePreviewHtml('');
                    setUploadedTemplateArrayBuffer(null);
                    setTemplateName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 text-sm font-medium text-white bg-redhalo-red rounded-md hover:opacity-95"
                >
                  {editingLetter ? 'Update Template' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Letter Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Generate - {letterTypes.find(t => t.id === activeLetterType)?.name}
              </h2>
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  setSelectedEmployee(null);
                  setSelectedTemplate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Template
                  </label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = letterTemplates[activeLetterType]?.find(t => t.id === e.target.value);
                      setSelectedTemplate(template);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  >
                    <option value="">Select a template</option>
                    {letterTemplates[activeLetterType]?.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Employee
                  </label>
                  <select
                    value={selectedEmployee?.id || ''}
                    onChange={(e) => {
                      const employee = employeesForGeneration.find(emp => String(emp.id) === String(e.target.value));
                      setSelectedEmployee(employee);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  >
                    <option value="">{employeesForGeneration.length > 0 ? 'Select an employee' : 'No employees available'}</option>
                    {employeesForGeneration.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name} - {employee.designation} ({employee.department})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  if (selectedTemplate && selectedEmployee) {
                    handlePreviewLetter(selectedTemplate, selectedEmployee);
                      setShowGenerateModal(false);
                  }
                }}
                disabled={!selectedTemplate || !selectedEmployee}
                className="px-4 py-2 text-sm font-medium text-redhalo-navy bg-redhalo-mint/60 rounded-md hover:bg-redhalo-mint disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Preview Letter
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowGenerateModal(false);
                    setSelectedEmployee(null);
                    setSelectedTemplate(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedTemplate && selectedEmployee) {
                      handleGenerateLetter(selectedTemplate, selectedEmployee);
                      setShowGenerateModal(false);
                      setSelectedEmployee(null);
                      setSelectedTemplate(null);
                    }
                  }}
                  disabled={!selectedTemplate || !selectedEmployee}
                  className="px-4 py-2 text-sm font-medium text-white bg-redhalo-red rounded-md hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate & Send PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showTemplatePreview && previewTemplateData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[98vw] max-w-[1400px] h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900">
                Template Preview - {previewTemplateData.name}
              </h2>
              <button
                onClick={() => {
                  setShowTemplatePreview(false);
                  setPreviewTemplateData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-200 bg-white">
              <p className="text-xs text-gray-600">
                {previewTemplateData.previewMode === 'uploaded'
                  ? `Word Preview Mode: ${previewTemplateData.useDocxRender ? 'Enabled (.docx)' : 'Unavailable'}`
                  : 'Template Preview Mode: Merged Template Content'}
              </p>
              {docxRenderFailed && (
                <p className="text-xs text-amber-700 mt-1">
                  Exact Word renderer returned blank output for this file. Showing fallback preview content.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-hidden bg-[#f1f3f5]">
              <div className="h-full overflow-auto">
                {previewTemplateData.useDocxRender && !docxRenderFailed ? (
                  <>
                    <div ref={docxStyleContainerRef} />
                    <div ref={docxPreviewContainerRef} className="min-h-full p-6 bg-white" />
                  </>
                ) : (
                  <div className="p-8">
                    {!previewTemplateData.useDocxRender && (
                      <div className="mb-4 max-w-xl bg-white border border-amber-200 rounded-lg p-4">
                        <p className="text-amber-700 font-medium mb-1">Exact Word preview is available only for .docx uploads</p>
                        <p className="text-sm text-gray-600">
                          Please upload a `.docx` file to preview the same layout with tables, numbering, fonts, header, footer, and logo.
                        </p>
                      </div>
                    )}
                    <div
                      className="bg-white border border-gray-200 rounded p-6 overflow-auto"
                      style={{ minHeight: '700px', lineHeight: '1.6' }}
                      dangerouslySetInnerHTML={{
                        __html:
                          previewTemplateData.richContentHtml ||
                          previewTemplateData.contentHtml ||
                          `<div style="white-space: pre-wrap;">${previewTemplateData.content || ''}</div>`
                      }}
                    />
                    {!previewTemplateData.contentHtml && !previewTemplateData.content && (
                      <div className="text-sm text-gray-600 mt-3">
                        No readable preview content was extracted from this file.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-white">
              <button
                onClick={() => {
                  setShowTemplatePreview(false);
                  setPreviewTemplateData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Letter Type Modal */}
      {showAddLetterType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add New Letter Type</h2>
              <button
                onClick={() => {
                  setShowAddLetterType(false);
                  setNewLetterTypeName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Letter Type Name
                </label>
                <input
                  type="text"
                  value={newLetterTypeName}
                  onChange={(e) => setNewLetterTypeName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  placeholder="Enter letter type name"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddLetterType(false);
                  setNewLetterTypeName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLetterType}
                className="px-4 py-2 text-sm font-medium text-white bg-redhalo-red rounded-md hover:opacity-95"
              >
                Add Letter Type
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LettersManager;
